import {
  buildLogbuchPdfHeader,
  buildLogbuchPdfStyles,
} from './logbuchPdfBuilder';
import { formatBerlinDate, formatBerlinDateTime } from './pdfDateTime';

function formatLocation(value) {
  if (value === 'nrw') {
    return 'NRW';
  }

  if (value === 'other') {
    return 'Andere Örtlichkeit';
  }

  return '-';
}

function formatWinchType(value) {
  if (value === 'winch_operation') {
    return 'Windeneinsatz';
  }

  if (value === 'winch_option') {
    return 'Windenoption';
  }

  return '-';
}

function countAssets(assets = [], kind) {
  return assets.filter((asset) => asset.asset_kind === kind).length;
}

function buildAttachmentSummary(entry) {
  const images = countAssets(entry.assets, 'image');
  const videos = countAssets(entry.assets, 'video');
  const documents = countAssets(entry.assets, 'document');
  return `Bilder: ${images} | Videos: ${videos} | Dokumente: ${documents}`;
}

function buildFilterSummary(filters = {}) {
  const yearText = Array.isArray(filters.yearLabels) && filters.yearLabels.length
    ? filters.yearLabels.join(', ')
    : 'Alle Jahre';
  const locationText = Array.isArray(filters.locationLabels) && filters.locationLabels.length
    ? filters.locationLabels.join(', ')
    : 'Alle Örtlichkeiten';
  const winchText = Array.isArray(filters.winchTypeLabels) && filters.winchTypeLabels.length
    ? filters.winchTypeLabels.join(', ')
    : 'Alle Arten';

  return `Jahre: ${yearText} | Örtlichkeit: ${locationText} | Art der Windenrettung: ${winchText}`;
}

function buildSummaryText(entries = []) {
  const nrw = entries.filter((entry) => entry.payload?.locationType === 'nrw').length;
  const other = entries.filter((entry) => entry.payload?.locationType === 'other').length;
  const winchOperation = entries.filter((entry) => entry.payload?.winchType === 'winch_operation').length;
  const winchOption = entries.filter((entry) => entry.payload?.winchType === 'winch_option').length;

  return `Einträge: ${entries.length} | NRW: ${nrw} | Andere Örtlichkeit: ${other} | Windeneinsatz: ${winchOperation} | Windenoption: ${winchOption}`;
}

function buildEntryColorAccent(displayColor) {
  if (!displayColor) {
    return null;
  }

  return {
    canvas: [
      {
        type: 'rect',
        x: 0,
        y: 0,
        w: 8,
        h: 64,
        color: displayColor,
      },
    ],
    width: 10,
    margin: [0, 2, 0, 0],
  };
}

function buildEntryBlock(entry, index) {
  const accent = buildEntryColorAccent(entry.display_color);
  const infoTable = {
    table: {
      widths: ['22%', '28%', '22%', '28%'],
      body: [
        [
          { text: 'Datum', bold: true },
          formatBerlinDate(entry.event_date),
          { text: 'Örtlichkeit', bold: true },
          formatLocation(entry.payload?.locationType),
        ],
        [
          { text: 'Art der Windenrettung', bold: true },
          formatWinchType(entry.payload?.winchType),
          { text: 'Zeitvorteil', bold: true },
          `${Number(entry.payload?.estimatedTimeAdvantageMinutes) || 0} Minuten`,
        ],
        [
          { text: 'Anhänge', bold: true },
          { text: buildAttachmentSummary(entry), colSpan: 3 },
          {},
          {},
        ],
        [
          { text: 'Letzte Änderung', bold: true },
          { text: formatBerlinDateTime(entry.updated_at), colSpan: 3 },
          {},
          {},
        ],
      ],
    },
    layout: 'lightHorizontalLines',
  };

  const blockContent = [
    {
      text: `${index + 1}. ${entry.title || 'Ohne Titel'}`,
      style: 'feedbackEntryTitle',
      margin: [0, 0, 0, 6],
    },
    infoTable,
    {
      text: 'Beschreibung',
      style: 'feedbackSectionLabel',
      margin: [0, 8, 0, 3],
    },
    {
      text: String(entry.description || '-'),
      style: 'feedbackBody',
      margin: [0, 0, 0, 3],
    },
  ];

  const links = Array.isArray(entry.payload?.links) ? entry.payload.links.filter(Boolean) : [];
  if (links.length) {
    blockContent.push(
      {
        text: 'Links',
        style: 'feedbackSectionLabel',
        margin: [0, 8, 0, 3],
      },
      {
        ul: links.map((link) => ({ text: link, link, color: '#1565C0' })),
        margin: [0, 0, 0, 0],
      },
    );
  }

  return {
    columns: [
      ...(accent ? [accent] : []),
      {
        width: '*',
        stack: blockContent,
      },
    ],
    columnGap: 10,
    margin: [0, 0, 0, 16],
  };
}

export function buildWindenrettungPdfFilename(exportDate = new Date()) {
  const safeDate = formatBerlinDate(exportDate, { fallback: formatBerlinDate(new Date()) });

  return `Windenrettung-Auswertung ${safeDate}.pdf`;
}

export function buildWindenrettungPdfDocument({
  entries = [],
  filters = {},
  brandingAssets = {},
  exportDate = new Date(),
} = {}) {
  const styles = {
    ...buildLogbuchPdfStyles(),
    feedbackMeta: { fontSize: 10, color: '#4b5563', margin: [0, 0, 0, 6] },
    feedbackEntryTitle: { fontSize: 13, bold: true, color: '#111827' },
    feedbackSectionLabel: { fontSize: 10, bold: true, color: '#374151' },
    feedbackBody: { fontSize: 10, color: '#111827', lineHeight: 1.25 },
    emptyState: { fontSize: 11, italics: true, color: '#6b7280' },
  };

  return {
    pageSize: 'A4',
    pageMargins: [36, 36, 36, 36],
    content: [
      buildLogbuchPdfHeader({ assets: brandingAssets, title: 'Windenrettung' }),
      {
        text: 'Export der aktuell sichtbaren Auswertungseinträge. Bilder, Videos und Dokumente werden nicht eingebettet; es werden nur die jeweiligen Anhangszahlen ausgewiesen.',
        style: 'feedbackMeta',
      },
      {
        text: buildFilterSummary(filters),
        style: 'feedbackMeta',
      },
      {
        text: `Exportiert am: ${formatBerlinDateTime(exportDate)}`,
        style: 'feedbackMeta',
      },
      {
        text: buildSummaryText(entries),
        style: 'sum',
        margin: [0, 0, 0, 12],
      },
      ...(entries.length
        ? entries.map((entry, index) => buildEntryBlock(entry, index))
        : [{ text: 'Für die aktuellen Filter sind keine Einträge vorhanden.', style: 'emptyState' }]),
    ],
    styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
}